import { Box, Typography, Stack, TextField } from "@mui/material";
import { Controller } from "react-hook-form";
import { CompanyAutocomplete, type CompanyOption } from "src/components/Selector/CompanySelector";
import type { Company } from "src/api/client-api/company/useGetOneOrganization.ts";
import type { CompanyGrid } from "src/api/client-api/company/useGetAllOrganization";
import type { Control, FieldErrors } from "react-hook-form";
import type { CompanyGridForm } from "./CompanySettingsForm";
import { useTranslation } from "react-i18next";
import type { RefObject } from "react";

type CompanyContactSectionProps = {
    control: Control<CompanyGridForm>;
    errors: FieldErrors<CompanyGridForm>;
    company?: Company | CompanyGrid;
    companyData?: Company | CompanyGrid;
    isSupplierMode: boolean;
    selectedParentsCache: RefObject<Map<string, CompanyOption>>;
};

export const CompanyContactSection = (props: CompanyContactSectionProps) => {
    const { control, errors, company, companyData, isSupplierMode, selectedParentsCache } = props;
    const { t } = useTranslation();

    return (
        <Box>
            <Typography variant="h4" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "32px" }}>
                Contact information
            </Typography>
            <Stack spacing="24px">
                {/* Address 1 and Address 2 */}
                <Box sx={{ display: "flex", gap: "24px" }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            Address 1 :
                        </Typography>
                        <Controller
                            name="address1"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter address 1"
                                    variant="outlined"
                                    error={Boolean(errors.address1)}
                                    helperText={errors.address1?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            Address 2 :
                        </Typography>
                        <Controller
                            name="address2"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter address 2"
                                    variant="outlined"
                                    error={Boolean(errors.address2)}
                                    helperText={errors.address2?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {/* City and State */}
                <Box sx={{ display: "flex", gap: "24px" }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            City :
                        </Typography>
                        <Controller
                            name="city"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter city"
                                    variant="outlined"
                                    error={Boolean(errors.city)}
                                    helperText={errors.city?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            State :
                        </Typography>
                        <Controller
                            name="state"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter state"
                                    variant="outlined"
                                    error={Boolean(errors.state)}
                                    helperText={errors.state?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {/* Country and Phone */}
                <Box sx={{ display: "flex", gap: "24px" }}>
                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            Country :
                        </Typography>
                        <Controller
                            name="country"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter country"
                                    variant="outlined"
                                    error={Boolean(errors.country)}
                                    helperText={errors.country?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>

                    <Box sx={{ flex: 1 }}>
                        <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                            Phone :
                        </Typography>
                        <Controller
                            name="phone"
                            control={control}
                            render={({ field: { ref, ...field } }) => (
                                <TextField
                                    {...field}
                                    inputRef={ref}
                                    placeholder="Enter phone number"
                                    variant="outlined"
                                    error={Boolean(errors.phone)}
                                    helperText={errors.phone?.message}
                                    sx={{
                                        width: "100%",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "24px",
                                            backgroundColor: "#FFFFFF",
                                            border: "1px solid #E7E6E4",
                                            height: "40px",
                                            "& fieldset": {
                                                border: "none",
                                            },
                                            "& input": {
                                                padding: "0px 12px 0px 20px",
                                                fontWeight: 500,
                                                fontSize: "0.875rem",
                                                color: "#393A36",
                                                "&::placeholder": {
                                                    color: "#A3A2A0",
                                                    opacity: 1,
                                                },
                                            },
                                        },
                                    }}
                                />
                            )}
                        />
                    </Box>
                </Box>

                {/* Email field */}
                <Box sx={{ width: "275px" }}>
                    <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                        Email* :
                    </Typography>
                    <Controller
                        name="email"
                        control={control}
                        render={({ field: { ref, ...field } }) => (
                            <TextField
                                {...field}
                                inputRef={ref}
                                placeholder="Enter email address"
                                variant="outlined"
                                type="email"
                                error={Boolean(errors.email)}
                                helperText={errors.email?.message}
                                sx={{
                                    width: "100%",
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "24px",
                                        backgroundColor: "#FFFFFF",
                                        border: "1px solid #E7E6E4",
                                        height: "40px",
                                        "& fieldset": {
                                            border: "none",
                                        },
                                        "& input": {
                                            padding: "0px 12px 0px 20px",
                                            fontWeight: 500,
                                            fontSize: "0.875rem",
                                            color: "#393A36",
                                            "&::placeholder": {
                                                color: "#A3A2A0",
                                                opacity: 1,
                                            },
                                        },
                                    },
                                }}
                            />
                        )}
                    />
                </Box>

                {/* Parent Companies field */}
                <Box sx={{ width: "100%" }}>
                    <Typography variant="h6" sx={{ lineHeight: "1.6em", color: "#393A36", marginBottom: "8px" }}>
                        {t("entreprises_parentes", "Parent Companies")} :
                    </Typography>
                    <Controller
                        name="parents"
                        control={control}
                        render={({ field: { onChange, value } }) => {
                            // Convert @id strings to CompanyOption objects for display
                            // using our cache to preserve newly selected companies
                            const selectedCompanies: CompanyOption[] = (value ?? [])
                                .map((parentId: string) => {
                                    // First, check the cache
                                    if (selectedParentsCache.current.has(parentId)) {
                                        return selectedParentsCache.current.get(parentId)!;
                                    }

                                    // Then try to find from existing company data
                                    if (companyData && (companyData as any).parents) {
                                        const parent = (companyData as any).parents.find(
                                            (p: any) => p["@id"] === parentId,
                                        );
                                        if (parent) {
                                            const companyOption: CompanyOption = {
                                                "@id": parent["@id"],
                                                id: parent.id,
                                                name: parent.name,
                                            };
                                            // Add to cache
                                            selectedParentsCache.current.set(parentId, companyOption);
                                            return companyOption;
                                        }
                                    }
                                    return null;
                                })
                                .filter((c): c is CompanyOption => c !== null);

                            return (
                                <CompanyAutocomplete
                                    value={selectedCompanies}
                                    onChange={(newCompanies) => {
                                        // Update cache with new selections
                                        newCompanies.forEach((company) => {
                                            selectedParentsCache.current.set(company["@id"], company);
                                        });
                                        // Send @id strings to form
                                        onChange(newCompanies.map((c) => c["@id"]));
                                    }}
                                    placeholder={t("company.selectParents", "Select parent companies")}
                                    excludeIds={company?.id ? [company.id] : []}
                                    source={isSupplierMode ? "supplier-parents" : "organizations"}
                                />
                            );
                        }}
                    />
                </Box>
            </Stack>
        </Box>
    );
};
